import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { authApi } from "../api/queries/auth";
import { ApiError } from "../api/client";

const Schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(8, "At least 8 characters"),
});
type Form = z.infer<typeof Schema>;

export function LoginPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Form>({ resolver: zodResolver(Schema) });

  const login = useMutation({
    mutationFn: (input: Form) => authApi.login(input),
    onSuccess: () => {
      qc.invalidateQueries();
      nav({ to: "/" });
    },
    onError: (e: ApiError) => {
      setError("root", { message: e.message ?? "Login failed" });
    },
  });

  const onSubmit = handleSubmit((v) => login.mutate(v));

  return (
    <main className="min-h-screen grid place-items-center bg-canvas px-4">
      <div className="card w-full max-w-[400px] p-6">
        <header className="mb-5 flex items-center gap-2">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              d="M16.5 6.5v9a4.5 4.5 0 0 1-9 0v-9a3 3 0 0 1 6 0v8a1.5 1.5 0 0 1-3 0v-7"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h1 className="text-[18px] font-semibold tracking-[-0.01em]">Sign in to Paperclip</h1>
        </header>
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <label className="field">
            <span className="field__label">Email</span>
            <input
              type="email"
              autoComplete="email"
              autoFocus
              className={`input${errors.email ? " input--err" : ""}`}
              {...register("email")}
              aria-invalid={!!errors.email}
            />
            {errors.email && <span className="field__error">{errors.email.message}</span>}
          </label>
          <label className="field">
            <span className="field__label">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              className={`input${errors.password ? " input--err" : ""}`}
              {...register("password")}
              aria-invalid={!!errors.password}
            />
            {errors.password && <span className="field__error">{errors.password.message}</span>}
          </label>
          {errors.root && (
            <div role="alert" className="rounded-sm bg-status-error/10 px-3 py-2 text-[12px] text-status-error">
              {errors.root.message}
            </div>
          )}
          <button className="btn btn--primary btn--lg" disabled={isSubmitting} type="submit">
            {isSubmitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <div className="mt-4 text-center text-[12px] text-secondary">
          Don’t have an account?{" "}
          <Link to="/signup" className="text-accent hover:underline">
            Create one
          </Link>
        </div>
      </div>
    </main>
  );
}
